import React, { useState, useEffect } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { messageError, messageToast } from '../../utils/define';
import { 
    updateAccountAuthInfoAction, 
    addAccountAuthInfoAction,
    updateAccountInfoAction,
    getAccountAuthInfoByIdAccountAction,
    getAccountInfoByIdAccountAction
} from '../../store/reducers/account';
import { Get } from '../../utils/httpProvider';
import { DONVI_API, CHUCVU_API, DOITUONG_API, CHUYENMON_API, BaseGatewayImage } from '../../utils/apiUrl';
import Select from 'react-select';
import InputFile from '../../components/input-file/InputFile';
import { acceptImage } from '../../utils/define';
import { getUserSub, isValidUrl } from '../../utils/helper';

const schema = yup.object().shape({
    email: yup
        .string()
        .email(messageError.emailMessage)
        .required(messageError.emptyMessage)
});

const SettingsTab = ({ isActive }) => {
    const itemAccount = useSelector((state) => state.account.itemAccount);
    const itemAccountAuthInfo = useSelector((state) => state.account.itemAccountAuthInfo);
    const itemAccountInfo = useSelector((state) => state.account.itemAccountInfo);
    const dispatch = useDispatch();

    const [donViOptions, setDonViOptions] = useState([]);
    const [chucVuOptions, setChucVuOptions] = useState([]);
    const [doiTuongOptions, setDoiTuongOptions] = useState([]);
    const [chuyenMonOptions, setChuyenMonOptions] = useState([]);

    useEffect(() => {
        const fetchCatalogs = async () => {
            try {
                const donViRes = await Get(DONVI_API.GET_PAGING, null, { PageSize: 1000 });
                if (donViRes?.data) setDonViOptions(donViRes.data.map(x => ({ value: x.id, label: x.tenDonVi })));

                const chucVuRes = await Get(CHUCVU_API.GET_PAGING, null, { PageSize: 1000 });
                if (chucVuRes?.data) setChucVuOptions(chucVuRes.data.map(x => ({ value: x.id, label: x.tenChucVu })));

                const doiTuongRes = await Get(DOITUONG_API.GET_PAGING, null, { PageSize: 1000 });
                if (doiTuongRes?.data) setDoiTuongOptions(doiTuongRes.data.map(x => ({ value: x.id, label: x.tenDoiTuong })));

                const chuyenMonRes = await Get(CHUYENMON_API.GET_PAGING, null, { PageSize: 1000 });
                if (chuyenMonRes?.data) setChuyenMonOptions(chuyenMonRes.data.map(x => ({ value: x.id, label: x.tenChuyenMon })));
            } catch (err) {
                console.error("Error fetching catalogs", err);
            }
        };
        fetchCatalogs();
    }, []);

    const handleUpdate = async (values, actions) => {
        try {
            let hasError = false;
            let userSub = await getUserSub();
            // Lấy First Name, Last Name (Required field của AuthInfo)
            let firstName = itemAccountAuthInfo?.firstName;
            let lastName = itemAccountAuthInfo?.lastName;
            let middleName = itemAccountAuthInfo?.middleName || '';
            
            if (!firstName || !lastName) {
                if (itemAccount?.fullName) {
                    const parts = itemAccount.fullName.trim().split(' ');
                    lastName = parts[0] || 'New';
                    firstName = parts.length > 1 ? parts[parts.length - 1] : 'User';
                    middleName = parts.length > 2 ? parts.slice(1, parts.length - 1).join(' ') : '';
                } else {
                    firstName = 'User';
                    lastName = 'New';
                }
            }

            // 1. Update AccountAuthInfo (Email, PhoneNumber)
            const authInfoData = {
                ...(itemAccountAuthInfo || {}),
                accountId: userSub,
                firstName: firstName,
                lastName: lastName,
                middleName: middleName,
                email: values.email,
                phoneNumber: values.phoneNumber,
                imagePath: values.imagePath,
                imageThumbnail: values.imageThumbnail
            };

            if (itemAccountAuthInfo) {
                const res1 = await dispatch(updateAccountAuthInfoAction({ id: itemAccountAuthInfo.id, data: authInfoData }));
                if (res1.payload?.errors) hasError = true;
            } else {
                const res1 = await dispatch(addAccountAuthInfoAction(authInfoData));
                if (res1.payload?.errors) hasError = true;
            }

            // 2. Update AccountInfo (DonVi, ChucVu, DoiTuong, ChuyenMon)
            const infoId = itemAccountInfo?.id || null;
            const accountInfoData = {
                accountId: userSub,
                doiTuongId: values.doiTuong?.value || "00000000-0000-0000-0000-000000000000",
                chucDanhChuyenMonId: values.chucDanhChuyenMon?.value || "00000000-0000-0000-0000-000000000000",
                lstDonViId: values.lstDonVi?.map(x => x.value) || [],
                lstChucVuId: values.lstChucVu?.map(x => x.value) || [],
                lstChuyenMonId: values.lstChuyenMon?.map(x => x.value) || []
            };

            const res2 = await dispatch(updateAccountInfoAction({ id: infoId, data: accountInfoData }));
            if (res2.payload?.errors) hasError = true;

            if (!hasError) {
                // Reload dữ liệu bên trái
                if (userSub) {
                    dispatch(getAccountAuthInfoByIdAccountAction(userSub));
                    dispatch(getAccountInfoByIdAccountAction(userSub));
                }
                toast.success(messageToast.messageToastUpdateSuccess);
            } else {
                toast.error("Có lỗi xảy ra khi cập nhật thông tin.");
            }
        } catch (error) {
            toast.error(messageToast.messageToastTryCatch);
        }
    };

    return (
        <div className={`tab-pane ${isActive ? 'active' : ''}`}>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values, actions) => handleUpdate(values, actions)}
                    initialValues={{
                        email: itemAccountAuthInfo?.email || itemAccount?.email || '',
                        phoneNumber: itemAccountAuthInfo?.phoneNumber || '',
                        lstDonVi: itemAccountInfo?.donVis?.map(d => ({ value: d.id, label: d.tenDonVi })) || [],
                        lstChucVu: itemAccountInfo?.chucVus?.map(c => ({ value: c.id, label: c.tenChucVu })) || [],
                        doiTuong: itemAccountInfo?.doiTuong ? { value: itemAccountInfo.doiTuong.id, label: itemAccountInfo.doiTuong.tenDoiTuong } : null,
                        chucDanhChuyenMon: itemAccountInfo?.chucDanhChuyenMon ? { value: itemAccountInfo.chucDanhChuyenMon.id, label: itemAccountInfo.chucDanhChuyenMon.tenDoiTuong } : null,
                        lstChuyenMon: itemAccountInfo?.chuyenMons?.map(c => ({ value: c.id, label: c.tenChuyenMon })) || [],
                        imagePath: itemAccountAuthInfo?.imagePath || '',
                        imageThumbnail: itemAccountAuthInfo?.imageThumbnail || ''
                    }}
                >
                    {({handleSubmit, handleChange, setFieldValue, values, errors}) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Ảnh đại diện
                                </Form.Label>
                                <Col sm="9">
                                    <InputFile
                                        enableReinitialize={true}
                                        values={
                                            values.imageThumbnail
                                                ? [
                                                    isValidUrl(values.imageThumbnail)
                                                        ? values.imageThumbnail
                                                        : BaseGatewayImage + '/' + values.imageThumbnail.replace(/^\//, '')
                                                ]
                                                : []
                                        }
                                        name="imageThumbnail"
                                        accept={acceptImage}
                                        labelImage="Chọn ảnh đại diện"
                                        onChange={(files) => {
                                            if (files.length > 0) {
                                                setFieldValue('imagePath', files[0].filePath, true);
                                                setFieldValue('imageThumbnail', files[0].thumbPath, true);
                                            } else {
                                                setFieldValue('imagePath', '', true);
                                                setFieldValue('imageThumbnail', '', true);
                                            }
                                        }}
                                    />
                                </Col>
                            </Form.Group>
                            
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Email
                                </Form.Label>
                                <Col sm="9">
                                    <Form.Control
                                        type="text"
                                        placeholder="Email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        isInvalid={!!errors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Số điện thoại
                                </Form.Label>
                                <Col sm="9">
                                    <Form.Control
                                        type="text"
                                        placeholder="Số điện thoại"
                                        name="phoneNumber"
                                        value={values.phoneNumber}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Đơn vị
                                </Form.Label>
                                <Col sm="9">
                                    <Select
                                        isMulti
                                        options={donViOptions}
                                        value={values.lstDonVi}
                                        onChange={(val) => setFieldValue('lstDonVi', val)}
                                        placeholder="Chọn đơn vị..."
                                    />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Chức vụ
                                </Form.Label>
                                <Col sm="9">
                                    <Select
                                        isMulti
                                        options={chucVuOptions}
                                        value={values.lstChucVu}
                                        onChange={(val) => setFieldValue('lstChucVu', val)}
                                        placeholder="Chọn chức vụ..."
                                    />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Chức danh chuyên môn
                                </Form.Label>
                                <Col sm="9">
                                    <Select
                                        isClearable
                                        options={doiTuongOptions}
                                        value={values.chucDanhChuyenMon}
                                        onChange={(val) => setFieldValue('chucDanhChuyenMon', val)}
                                        placeholder="Chọn chức danh chuyên môn trong GPHN"
                                    />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Chức danh đang hoạt động
                                </Form.Label>
                                <Col sm="9">
                                    <Select
                                        isClearable
                                        options={doiTuongOptions}
                                        value={values.doiTuong}
                                        onChange={(val) => setFieldValue('doiTuong', val)}
                                        placeholder="Chọn chức danh đang hoạt động tại bệnh viện"
                                    />
                                </Col>
                            </Form.Group>

                            {/* <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-3 col-form-label">
                                    Chuyên môn
                                </Form.Label>
                                <Col sm="9">
                                    <Select
                                        isMulti
                                        options={chuyenMonOptions}
                                        value={values.lstChuyenMon}
                                        onChange={(val) => setFieldValue('lstChuyenMon', val)}
                                        placeholder="Chọn chuyên môn..."
                                    />
                                </Col>
                            </Form.Group> */}

                            <Form.Group as={Row} className="mb-3">
                                <Col md={{span: 9, offset: 3}}>
                                    <Button type="submit" theme="danger">
                                        Cập nhật
                                    </Button>
                                </Col>
                            </Form.Group>

                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </div>
    );
};

export default SettingsTab;
